import { mapFields } from 'vuex-map-fields'

const REWARD_LABELS = {
  prospect_leads: 'Prospect Referrals'
}

export default {
  data() {
    return {
      onboardingAuthRetryPending: false,
    }
  },
  computed: {
    ...mapFields('onboarding_state', [
      'onboardingData',
      'currentProgress',
      'currentLevelInfo',
      'nextLevelInfo',
      'nextLevelWithAtLeastOneIncompleteTask',
      'activeRequest',
      'viewLevel',
      'pendingReward'
    ]),
    stillOnboarding() {
      let stillInProgress = false;
      if (this.onboardingData && this.onboardingData?.length > 0) {
        this.onboardingData?.forEach((level) => {
          level?.features?.forEach((feature) => {
            feature?.tasks?.forEach((task) => {
              if (!(task.complete || task.skipped)) {
                stillInProgress = true;
              }
            })
          })
        })
      }
      return stillInProgress;
    }
  },
  methods: {
    async getOnboardingData() {
      if (this.activeRequest) {
        return this.activeRequest;
      }

      const currentUser = this.$store?.getters?.['current_user/getCurrentUser']
      if (!currentUser?.id) {
        return null
      }

      try {
        this.activeRequest = this.$http.post('/api/v1/onboarding/process-onboarding-data');
        const response = await this.activeRequest;
        
        if(response.data?.data) {
          const previousLevels = this.onboardingData;
          const processedData = this.transformOnboardingData(response.data.data);
          const unlocked = this.findNewlyUnlockedReward(previousLevels, processedData.levels);
          if (unlocked && !this.pendingReward) {
            this.pendingReward = unlocked;
          }
          this.onboardingData = processedData.levels;
          this.currentProgress = processedData.currentProgress;
          this.currentLevelInfo = processedData.currentLevelInfo;
          this.nextLevelInfo = processedData.nextLevelInfo;
          this.nextLevelWithAtLeastOneIncompleteTask = processedData.nextLevelWithAtLeastOneIncompleteTask;
        } else {
          this.onboardingData = [];
          this.currentProgress = null;
          this.currentLevelInfo = null;
          this.nextLevelInfo = null;
          this.nextLevelWithAtLeastOneIncompleteTask = null;
        }
        
        return response;
      } catch (error) {
        if (error?.status === 401 && currentUser?.id && !this.onboardingAuthRetryPending) {
          this.onboardingAuthRetryPending = true;
          setTimeout(() => {
            this.onboardingAuthRetryPending = false;
            if (this.$store?.getters?.['current_user/getCurrentUser']?.id) {
              this.getOnboardingData();
            }
          }, 2000);
        } else if (error?.status !== 401) {
          console.error('Error fetching onboarding data:', error);
        }
        this.activeRequest = null;
        return null;
      } finally {
        this.activeRequest = null;
      }
    },

    transformOnboardingData(rawData) {
        const levelsMap = new Map();
        let currentProgress = {
          currentLevel: null,
          totalProgress: 0,
          hasUnfinishedFeature: false
        };
        let currentLevelInfo = null;
        let nextLevelInfo = null;
        let nextLevelWithAtLeastOneIncompleteTask = null;
  
        // Step 1: Group data by level and feature
        rawData.forEach(item => {
          const levelId = item.onboarding_level_table_id;
          const featureId = item.onboarding_feature_table_id;
          const task = {
            id: item.onboarding_task_table_id,
            name: item.task_name,
            sort_order: item.task_sort_order,
            complete: item.task_complete == 1,
            skipped: item.task_skipped == 1,
            link: item.link,
            task_type_key: item.task_type_key,
            question_id: item.question_id || null,
            university_lesson_id: item.university_lesson_id,
            lesson_video_url: item.lesson_video_url || null,
            video_cta_label: item.video_cta_label || null,
            is_skippable: item.is_skippable,
            reward_type: item.reward_type,
            reward_value: item.reward_value
          };
  
          // Create level if not exists
          if (!levelsMap.has(levelId)) {
            levelsMap.set(levelId, {
              id: levelId,
              name: item.level_name,
              icon: item.level_icon,
              sort_order: item.level_sort_order,
              rewards: item.rewards,
              featuresMap: new Map()
            });
          }
  
          const level = levelsMap.get(levelId);
  
          // Create feature if not exists
          if (!level.featuresMap.has(featureId)) {
            level.featuresMap.set(featureId, {
              id: featureId,
              name: item.feature_name,
              icon: item.feature_icon,
              sort_order: item.feature_sort_order,
              time_to_complete: item.time_to_complete,
              tasks: []
            });
          }
  
          const feature = level.featuresMap.get(featureId);
          feature.tasks.push(task);
        });
  
        // Step 2: Flatten and mark complete flags
        const sortedLevels = Array.from(levelsMap.values())
          .sort((a, b) => a.sort_order - b.sort_order)
          .map(level => {
            const features = Array.from(level.featuresMap.values())
              .sort((a, b) => a.sort_order - b.sort_order)
              .map(feature => {
                feature.tasks.sort((a, b) => a.sort_order - b.sort_order);
                // Check if all tasks in the feature are incomplete
                const allTasksIncomplete = feature.tasks.every(t => !t.complete && !t.skipped);
                if (allTasksIncomplete) {
                  currentProgress.hasUnfinishedFeature = true;
                }
                feature.feature_complete = feature.tasks.every(t => t.complete || t.skipped);
                return feature;
              });
  
            level.features = features;
            delete level.featuresMap;
            level.level_complete = features.every(f => f.feature_complete);
            return level;
          });
  
        // Step 3: Determine locked/unlocked status and current level
        let levelUnlocked = true;
        let foundCurrent = false;
  
        sortedLevels.forEach((level, levelIndex) => {
          level.is_locked = !levelUnlocked;
          
          // Group features by sort_order
          const featuresByOrder = level.features.reduce((acc, feature) => {
            if (!acc[feature.sort_order]) {
              acc[feature.sort_order] = [];
            }
            acc[feature.sort_order].push(feature);
            return acc;
          }, {});
  
          // Process features by sort_order groups
          let featureUnlocked = !level.is_locked;
          Object.values(featuresByOrder).forEach(featureGroup => {
            // Set locked status for all features in the group
            featureGroup.forEach(feature => {
              feature.is_locked = !featureUnlocked;
            });
            
            // Only unlock next group if ALL features in current group are complete
            const allFeaturesComplete = featureGroup.every(feature => feature.feature_complete);
            featureUnlocked = allFeaturesComplete;
          });
  
          // If we haven't found the current level and this level isn't complete
          if (!foundCurrent && !level.level_complete && this.viewLevel === null) {
            foundCurrent = true;
            currentProgress.currentLevel = level.name;
  
            // Calculate progress for current level only
            let levelTotalTasks = 0;
            let levelCompletedTasks = 0;
  
            level.features.forEach(feature => {
              feature.tasks.forEach(task => {
                levelTotalTasks++;
                if (task.complete || task.skipped) {
                  levelCompletedTasks++;
                }
              });
            });
  
            // Calculate progress percentage for current level
            currentProgress.totalProgress = levelTotalTasks > 0
              ? Math.round((levelCompletedTasks / levelTotalTasks) * 100)
              : 0;
  
            // Set currentLevelInfo
            currentLevelInfo = {
              id: level.id,
              name: level.name,
              icon: level.icon,
              rewards: level.rewards,
              sort_order: level.sort_order,
              features: level.features,
              is_locked: level.is_locked,
              level_complete: level.level_complete
            };
  
            // Set nextLevelInfo if there is a next level
            if (levelIndex < sortedLevels.length - 1) {
              const nextLevel = sortedLevels[levelIndex + 1];
              nextLevelInfo = {
                name: nextLevel.name,
                features: nextLevel.features
              };

              // Find next level with incomplete task
              for (let i = levelIndex + 1; i < sortedLevels.length; i++) {
                const candidateLevel = sortedLevels[i];
                const hasIncompleteTask = candidateLevel.features.some(feature => 
                  feature.tasks.some(task => !task.complete && !task.skipped)
                );
                
                if (hasIncompleteTask) {
                  nextLevelWithAtLeastOneIncompleteTask = {
                    name: candidateLevel.name,
                    features: candidateLevel.features
                  };
                  break;
                }
              }
            } else {
              nextLevelInfo = {}; // No next level available
              nextLevelWithAtLeastOneIncompleteTask = {}; // No next level with incomplete tasks available
            }
          } else if (this.viewLevel === level.id) {
            currentProgress.currentLevel = level.name;

            // Calculate progress for current level only
            let levelTotalTasks = 0;
            let levelCompletedTasks = 0;

            level.features.forEach(feature => {
              feature.tasks.forEach(task => {
                levelTotalTasks++;
                if (task.complete || task.skipped) {
                  levelCompletedTasks++;
                }
              });
            });

            // Calculate progress percentage for current level
            currentProgress.totalProgress = levelTotalTasks > 0
              ? Math.round((levelCompletedTasks / levelTotalTasks) * 100)
              : 0;

            // Set temp currentLevelInfo for viewing
            currentLevelInfo = {
              id: level.id,
              name: level.name,
              icon: level.icon,
              rewards: level.rewards,
              sort_order: level.sort_order,
              features: level.features,
              is_locked: level.is_locked,
              level_complete: level.level_complete,
            };
            nextLevelInfo = {};
            nextLevelWithAtLeastOneIncompleteTask = {};
          }
  
          levelUnlocked = level.level_complete ? levelUnlocked : false;
        });
  
        // If everything is complete, set last level as current and 100% progress
        if (!currentProgress.currentLevel) {
          const lastLevel = sortedLevels[sortedLevels.length - 1];
          if (lastLevel) {
            currentProgress.currentLevel = lastLevel.name;
            currentProgress.totalProgress = 100;
            currentLevelInfo = {
              id: lastLevel.id,
              name: lastLevel.name,
              icon: lastLevel.icon,
              rewards: lastLevel.rewards,
              sort_order: lastLevel.sort_order,
              features: lastLevel.features,
              is_locked: lastLevel.is_locked,
              level_complete: lastLevel.level_complete
            };
            nextLevelInfo = {}; // No next level since we're at the last level
            nextLevelWithAtLeastOneIncompleteTask = {}; // No next level with incomplete tasks available
          }
        }
  
        return {
          levels: sortedLevels,
          currentProgress,
          currentLevelInfo,
          nextLevelInfo,
          nextLevelWithAtLeastOneIncompleteTask
        };
      },

    findNewlyUnlockedReward(oldLevels, newLevels) {
      if (!Array.isArray(oldLevels) || oldLevels.length === 0) return null;

      const oldComplete = new Map();
      oldLevels.forEach(level => level?.features?.forEach(feature =>
        feature?.tasks?.forEach(task => oldComplete.set(task.id, !!task.complete))
      ));

      for (const level of newLevels) {
        for (const feature of level.features || []) {
          for (const task of feature.tasks || []) {
            const wasComplete = oldComplete.get(task.id) || false;
            if (!wasComplete && task.complete && task.reward_type && Number(task.reward_value) > 0) {
              const key = String(task.reward_type).split(':').pop();
              const displayName = REWARD_LABELS[key]
                || key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
              return { name: `${task.reward_value} ${displayName}` };
            }
          }
        }
      }
      return null;
    },

    /**
     * Update a task or level's completion status
     * @param {Object} params - Parameters object
     * @param {number|null} params.taskId - Task ID to update (null if updating level)
     * @param {number|null} params.levelId - Level ID to update (null if updating task)
     */
    async updateTask({taskId, levelId}) {
      try {
        if (this.onboardingData && this.onboardingData?.length > 0) {
          this.onboardingData?.forEach((level, levelIndex) => {
            level?.features?.forEach((feature, featureIndex) => {
              feature?.tasks?.forEach((task, taskIndex) => {
                if(task?.id == taskId && taskId !== null){
                  this.onboardingData[levelIndex].features[featureIndex].tasks[taskIndex].skipped = true;
                }
              });
            });
          });
          
          // Make API call to update the task/level
          let postData = {};
          if(taskId !== null){
            postData.onboardingTaskId = taskId;
          } else if (levelId !== null) {
            postData.levelId = levelId;
          }

          const response = await this.$http.post('/api/v1/onboarding/updateTask', postData);
          
          return response;
        } else {
          console.error('onboardingData is empty or has an unexpected structure');
        }
      } catch (error) {
        console.error('Error updating task:', error);
        this.$noty.error('Error updating task');
      }
    }
  }
} 